import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from "@ant-design/icons"
import { Button, Result, Typography } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { type RootState } from "../../store"
import { handleSaveResult } from "../../utils/handlers/handle-save-result"
import { RESULT_TYPES } from "../Settings/constants"
import { setSettingsProp } from "../Settings/redux/settings.slice"
import { confirmEmail } from "../Settings/redux/settings.thunks"

const { Text, Title } = Typography

function ConfirmChangeEmailPage({
  userId,
  saveResult,
  saveFetching,
  setProp,
  confirmChangeEmail
}: ConnectedProps<typeof connector>) {
  const { token } = useParams()
  const navigate = useNavigate()

  const handleBackToHome = () => {
    if (userId) {
      navigate("/settings")
    } else {
      navigate("/login")
    }
  }

  const handleValidateToken = async () => {
    await confirmChangeEmail({ token })
  }

  useEffect(() => {
    void handleValidateToken()
  }, [])

  useEffect(() => {
    if (saveResult?.type === RESULT_TYPES.EMAIL_UPDATE_CONFIRM) {
      handleSaveResult(saveResult)

      setProp({ key: "saveResult", value: false })
    }
  }, [saveResult])

  if (!token) {
    navigate("/")
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg">
        <Result
          rootClassName={"max-sm:px-3"}
          icon={
            saveFetching ? (
              <LoadingOutlined className="!text-5xl" />
            ) : (
              <CheckCircleOutlined className="!text-green-500 text-5xl" />
            )
          }
          title={
            <Title level={3} className="font-semibold">
              {saveFetching
                ? "Email verification is being carried out"
                : "Email has been successfully changed!"}
            </Title>
          }
          subTitle={
            <Text className="text-sm sm:text-base text-gray-600 mt-2 block">
              {saveFetching
                ? "Email is being updated. After that, you will be able to use it."
                : "Your email has been successfully updated. Now you can use a new address to log in."}
            </Text>
          }
          extra={
            <Button
              type="primary"
              size="large"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToHome}
              loading={saveFetching}
              className="w-full sm:w-auto mt-4 h-10 sm:h-12 text-sm sm:text-base font-medium"
            >
              Return
            </Button>
          }
          className="text-center"
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo?.id,
  saveResult: state.settings.saveResult,
  saveFetching: state.settings.saveFetching
})

const mapDispatchToProps = {
  confirmChangeEmail: confirmEmail,
  setProp: setSettingsProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(ConfirmChangeEmailPage)
